import { useState, useEffect } from "react";
import {
  Download,
  Eye,
  EyeOff,
  Star,
  ToggleLeft,
  ToggleRight,
  SquareX,
} from "lucide-react";
import {
  getUserProfile,
  getUserMessage,
  getMatchScores,
  submitTailoringRating,
} from "./services/api";
import { useToast } from "./components/ToastContext";

const TailorResumeScreen = ({
  tailoredResume,
  tailoredResumeDocx,
  ats,
  tailoredChanges,
  jobDescription,
  setJobDescription,
  isLoading,
  isGeneratingCoverLetter,
  handleTailorResume,
  handleDownloadCoverLetter,
  handleDownloadPdf,
  handleSaveJobApplication,
  subscriptionCounts,
  setSubscriptionCounts,
  userEmail,
  navigateTo,
  resetTailorState,
  handleErrors,
}) => {
  const toast = useToast();
  const [resumeData, setResumeData] = useState({});
  const [selectedResume, setSelectedResume] = useState("");
  const [showChanges, setShowChanges] = useState(true);
  const [pdfView, setPdfView] = useState(false);
  const [saveComplete, setSaveComplete] = useState(false);
  const [downloadComplete, setDownloadComplete] = useState(false);
  const [downloadedApplicationDetails, setDownloadedApplicationDetails] =
    useState(null);
  const [bestResume, setBestResume] = useState("");
  const [isFetchingScores, setIsFetchingScores] = useState(false);
  const [matchScores, setMatchScores] = useState({});
  const [tailoringRating, setTailoringRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [isSubmittingRating, setIsSubmittingRating] = useState(false);
  const hasCoverLetterAllowance =
    subscriptionCounts.cover_letters_limit -
      subscriptionCounts.cover_letters_generated >
    0;

  useEffect(() => {
    // Check if resume exists in database
    const handleGetCurrentResume = async () => {
      const userInfo = await getUserProfile(userEmail, true, true, false, true);
      setResumeData(userInfo.resumeData || {});
      setSelectedResume(Object.keys(userInfo.resumeData || {})[0] || "");
    };
    // Check if resume exists in database
    handleGetCurrentResume();
  }, [userEmail]);

  const fetchMatchScores = async () => {
    setIsFetchingScores(true);
    try {
      const scores = await getMatchScores(userEmail, jobDescription);
      setMatchScores(scores);
      setBestResume(
        Object.keys(scores).length > 0
          ? Object.keys(scores).reduce((a, b) =>
              scores[a] > scores[b] ? a : b,
            )
          : "",
      );
      setSelectedResume(
        Object.keys(scores).length > 0
          ? Object.keys(scores).reduce((a, b) =>
              scores[a] > scores[b] ? a : b,
            )
          : "",
      );
    } catch (error) {
      console.error("Error fetching match scores:", error);
    } finally {
      setIsFetchingScores(false);
    }
  };

  const tailorResume = async () => {
    setPdfView(true);
    setShowChanges(true);
    setDownloadComplete(false);
    setDownloadedApplicationDetails(null);
    setSaveComplete(false);
    setTailoringRating(0);
    setHoveredRating(0);
    const { tailoredData, details } = await handleTailorResume(selectedResume, {
      matchScores: matchScores,
    });
    if (tailoredData === null || details === null) {
      toast.error(
        "We couldn't tailor your resume. Please try again in a moment.",
      );
      setPdfView(false);
    } else {
      setSubscriptionCounts({
        ...subscriptionCounts,
        resumes_used: subscriptionCounts.resumes_used + 1,
      });
    }
  };

  const submitRating = async (rating) => {
    if (isSubmittingRating) return;

    setTailoringRating(rating);
    setHoveredRating(0);
    setIsSubmittingRating(true);
    try {
      await submitTailoringRating({
        email: userEmail,
        rating,
        selectedResume,
        jobDescription,
        jobTitle: "",
        atsScore: ats,
      });
      toast.success("Thanks for the feedback.");
    } catch (error) {
      setTailoringRating(0);
      toast.error(getUserMessage(error));
      handleErrors("Submit tailoring rating", error);
    } finally {
      setIsSubmittingRating(false);
    }
  };

  const handleGenerateCoverLetterClick = () => {
    if (!hasCoverLetterAllowance) {
      toast.error(
        "Out of cover letter allowance. Upgrade your subscription for more.",
      );
      return;
    }

    handleDownloadCoverLetter(
      resumeData[selectedResume] || selectedResume,
      jobDescription,
      downloadedApplicationDetails || undefined,
    );
  };

  const handleDownloadTailoredResume = async (format = "docx") => {
    try {
      const useDocx = format === "docx" && tailoredResumeDocx;
      const details = await handleDownloadPdf(
        false,
        useDocx ? tailoredResumeDocx : tailoredResume,
        undefined,
        undefined,
        useDocx ? "docx" : "pdf",
      );
      setDownloadedApplicationDetails(details);
      setDownloadComplete(true);
    } catch (error) {
      toast.error(getUserMessage(error));
      handleErrors("Download tailored resume", error);
    }
  };

  const handleSaveApplication = async () => {
    if (!downloadComplete) {
      toast.info(
        "Please download the generated resume before saving this application.",
      );
      return;
    }
    try {
      await handleSaveJobApplication(downloadedApplicationDetails || undefined);
      setSaveComplete(true);
      toast.success(
        "Application saved. You can view it in your application history.",
      );
    } catch (error) {
      toast.error(getUserMessage(error));
      await handleErrors("Save job application", error);
    }
  };

  return (
    <div className="flex-1 bg-gray-50 p-4">
      <div className="h-full max-w-6xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg p-4">
          <div className="items-center">
            <h2 className="applicant-theme-text">Tailor Your Resume</h2>
            <h2 className="applicant-theme-text relative w-full py-2">
              {matchScores[selectedResume] !== undefined && (
                <span className="text-sm absolute right">
                  Resume Match: {matchScores[selectedResume]}%
                </span>
              )}
            </h2>
          </div>
          <div className="mobile-toggle-container">
            <button
              onClick={() => setPdfView(!pdfView)}
              className="shared-toggle-btn"
            >
              <span className={!pdfView ? "active" : ""}>Job Description</span>
              {!pdfView ? (
                <ToggleLeft className="text-indigo-600" />
              ) : (
                <ToggleRight className="text-indigo-600" />
              )}
              <span className={pdfView ? "active" : ""}>Resume Preview</span>
            </button>
          </div>
          <div className="flex gap-4">
            {!pdfView ? (
              <div className="box-half-align relative">
                <textarea
                  value={jobDescription || ""}
                  onChange={(e) => {
                    resetTailorState();
                    setJobDescription(e.target.value);
                  }}
                  placeholder="Paste the job description here to tailor your resume..."
                  className="w-full p-4 border rounded-lg h-display-box"
                />
                {/* Translucent popup for tailored changes */}
                <div className="grid grid-cols-3 gap-4">
                  <button
                    onClick={tailorResume}
                    disabled={isLoading || !jobDescription}
                    className="buttons-indigo"
                  >
                    Tailor Resume
                  </button>
                  <select
                    className="dropdown-select"
                    value={selectedResume || bestResume}
                    disabled={isLoading || !jobDescription}
                    onChange={(e) => setSelectedResume(e.target.value)}
                  >
                    {Object.keys(resumeData).map((name) => (
                      <option key={name} value={name}>
                        {name}
                        {bestResume === name ? " - Best Match" : ""}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => fetchMatchScores()}
                    disabled={isFetchingScores || !jobDescription}
                    className="buttons-navigation"
                  >
                    {isFetchingScores ? "Finding..." : "Find Best Resume"}
                  </button>
                </div>
                <h3 className="text-sm text-gray-500 right absolute">
                  Resumes tailored: {subscriptionCounts.resumes_used}/{" "}
                  {subscriptionCounts.resumes_limit}
                </h3>
              </div>
            ) : (
              <div className="box-half-align relative">
                {!isLoading && tailoredResume ? (
                  <>
                    <div className="border rounded-lg mb-2 tailored-resume-display">
                      <iframe
                        src={`data:application/pdf;base64,${tailoredResume}`}
                        className="w-full h-full"
                        title="Resume PDF"
                      />
                    </div>
                  </>
                ) : (
                  <div className="border rounded-lg mb-2 tailored-resume-display">
                    {isLoading ? (
                      <div className="buttons-container">
                        <img
                          src="/assets/Applicant-tailoring.png"
                          className="w-25 h-25 max-auto mb-2"
                          alt="Applicant tailoring logo"
                        />
                      </div>
                    ) : (
                      <div className="buttons-container text-gray-500">
                        You will see your tailored resume here.
                      </div>
                    )}
                  </div>
                )}
                {!isLoading && tailoredResume && (
                  <>
                    {showChanges ? (
                      <div className="bg-opacity-50 absolute inset-0 rounded-md shadow-lg h-display-box px-6 flex justify-center items-center relative">
                        <div className="justify-center text-sm text-gray-600 font-semibold overflow-auto rounded-md tailored-changes-modal">
                          <div className="flex justify-between">
                            <h3 className="text-lg font-bold text-gray-900">
                              Tailored Changes
                            </h3>
                            <h3 className="text-lg font-bold text-gray-900">
                              ATS Score: {ats}%
                            </h3>
                          </div>

                          {typeof tailoredChanges === "string" ? (
                            <p
                              className="whitespace-pre-wrap"
                              style={{ whiteSpace: "pre-line" }}
                            >
                              {tailoredChanges}
                            </p>
                          ) : (
                            <pre className="whitespace-pre-wrap">
                              {JSON.stringify(tailoredChanges, null, 2)}
                            </pre>
                          )}
                        </div>
                        <button
                          className="buttons-red-mini top-0 right ml-auto m-sm absolute"
                          onClick={() => setShowChanges(false)}
                        >
                          <SquareX className="min-w-2 min-h-2" />
                        </button>
                      </div>
                    ) : null}
                    <div className="flex flex-col gap-4">
                      <div className="grid grid-cols-3 gap-2 justify-items">
                        <button
                          onClick={() => {
                            setPdfView(false);
                            setMatchScores({});
                            setSaveComplete(false);
                            setDownloadComplete(false);
                            setDownloadedApplicationDetails(null);
                            setTailoringRating(0);
                            setHoveredRating(0);
                            resetTailorState();
                          }}
                          className="buttons-indigo"
                          disabled={isLoading || isGeneratingCoverLetter}
                        >
                          Try Another Job
                        </button>
                        <button
                          onClick={() => handleSaveApplication()}
                          className="buttons-green"
                          disabled={saveComplete}
                        >
                          Save Application
                        </button>
                        <button
                          onClick={handleGenerateCoverLetterClick}
                          className={`buttons-indigo ${!hasCoverLetterAllowance ? "opacity-60" : ""}`}
                          disabled={isGeneratingCoverLetter}
                          aria-disabled={!hasCoverLetterAllowance}
                        >
                          {isGeneratingCoverLetter ? (
                            <>Generating...</>
                          ) : (
                            <>Generate Cover Letter</>
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="w-full flex flex-col gap-2">
                      <div
                        className="w-full flex gap-2"
                        data-testid="tailor-action-row"
                      >
                        <button
                          onClick={() => setShowChanges(!showChanges)}
                          className="tailor-action-btn"
                          title={showChanges ? "Hide changes" : "Show changes"}
                        >
                          {!showChanges ? (
                            <>
                              <Eye className="w-4 h-4" />
                            </>
                          ) : (
                            <>
                              <EyeOff className="w-4 h-4" />
                            </>
                          )}
                        </button>

                        <button
                          onClick={() => handleDownloadTailoredResume("docx")}
                          className="tailor-action-btn"
                          disabled={!tailoredResumeDocx}
                          title="Download ATS-friendly DOCX"
                        >
                          <Download className="w-4 h-4" />
                          <span>DOCX</span>
                        </button>
                        <button
                          onClick={() => handleDownloadTailoredResume("pdf")}
                          className="tailor-action-btn"
                          title="Download PDF preview"
                        >
                          <Download className="w-4 h-4" />
                          <span>PDF</span>
                        </button>
                      </div>
                      <p className="text-xxs text-gray-700">
                        How would you rate the tailored resume?
                      </p>
                      <div
                        className="w-full flex items-center gap-2"
                        data-testid="tailoring-rating-bar"
                      >
                        {[1, 2, 3, 4, 5].map((rating) => (
                          <button
                            key={rating}
                            type="button"
                            onClick={() => submitRating(rating)}
                            onMouseEnter={() => setHoveredRating(rating)}
                            onMouseLeave={() => setHoveredRating(0)}
                            onFocus={() => setHoveredRating(rating)}
                            onBlur={() => setHoveredRating(0)}
                            disabled={isSubmittingRating}
                            className="p-2 text-indigo-600 rounded-md"
                            title={`Rate ${rating} out of 5`}
                            aria-label={`Rate tailoring ${rating} out of 5`}
                          >
                            <Star
                              className="w-4 h-4"
                              data-testid={`rating-star-${rating}`}
                              fill={
                                (hoveredRating || tailoringRating) >= rating
                                  ? "currentColor"
                                  : "none"
                              }
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TailorResumeScreen;
